import { useMemo } from 'react';

// Material UI
import {
    styled,
    Box,
    Grid,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
} from '@mui/material';
import { Alert as _Alert } from '@mui/material';

// Components
import { Page } from 'components/ui/layout';
import { AlertButton } from 'components/ui/alerts';
import HostPricingDetails from 'components/HostPricingDetails';
import EventPayouts from 'components/payouts/EventPayouts';
import RecentOrders from 'components/widgets/RecentOrders';
import ProductSalesReport from 'components/widgets/ProductSalesReport';
import ConnectAlert from 'components/ConnectAlert';
import SeatingChart from 'components/SeatingChart';

// Utility
import { formatPrice } from 'utility/numbers';
import { useEvent, useEventDispatch } from 'context/event';
import moment from 'moment-timezone';
import SeasonEventClose from './SeasonEventClose';

import { useEventDashboardGuestCheckins } from './dashboard/hooks/useEventDashboardGuestCheckins';
import { useEventDashboardOrders } from './dashboard/hooks/useEventDashboardOrders';
import { useEventDashboardOverview } from './dashboard/hooks/useEventDashboardOverview';
import { useEventDashboardRevenue } from './dashboard/hooks/useEventDashboardRevenue';
import { useEventDashboardTicketsSold } from './dashboard/hooks/useEventDashboardTicketsSold';

export default function EventDashboard(props) {
    const eventId = props.match.params.id;
    const numericEventId = Number(eventId);
    const timezone = useMemo(() => moment.tz.guess(true), []);
    const event = useEvent();
    const dispatch = useEventDispatch();

    const overviewQuery = useEventDashboardOverview({ eventId: numericEventId, timezone });
    const revenueQuery = useEventDashboardRevenue({ eventId: numericEventId, timezone });
    const ordersQuery = useEventDashboardOrders({ eventId: numericEventId, timezone });
    const ticketsSoldQuery = useEventDashboardTicketsSold({ eventId: numericEventId, timezone });
    const guestCheckinsQuery = useEventDashboardGuestCheckins({
        eventId: numericEventId,
        timezone,
    });

    const stats = overviewQuery.data;

    if (overviewQuery.isPending) {
        return (
            <Page>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    mt={12}
                >
                    <CircularProgress />
                </Box>
            </Page>
        );
    }

    if (overviewQuery.isError) {
        return (
            <Page>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    mt={12}
                >
                    <Typography variant="h5">There was a problem finding your event</Typography>
                    <Box height={32} />
                    <Button variant="outlined" onClick={() => props.history.push('/events')}>
                        Back to Events
                    </Button>
                </Box>
            </Page>
        );
    }

    let publishStatus = 'draft';
    let alertType;
    let alertText;

    if (event.live_flag) {
        event.unlisted_flag ? (publishStatus = 'unlisted') : (publishStatus = 'published');
    }

    const endDate = moment.tz(event.end_date, event.time_zone);
    const currentDate = moment().tz(event.time_zone);

    if (endDate < currentDate) {
        publishStatus = 'concluded';
    }

    switch (publishStatus) {
        case 'draft':
            alertType = 'warning';
            alertText = 'Your event is in draft mode';
            break;
        case 'unlisted':
            alertType = 'info';
            alertText = 'Your event is unlisted';
            break;
        case 'published':
            alertType = 'success';
            alertText = 'Your event is published';
            break;
        case 'concluded':
            alertType = 'info';
            alertText = 'Your event has concluded';
            break;

        default:
            break;
    }

    return (
        <Page>
            <Box mb={4}>
                <SeasonEventClose
                    event={event}
                    onClosed={(data) =>
                        dispatch({
                            type: 'set_event',
                            event: {
                                ...event,
                                seats_closed_at: data.seats_closed_at || moment.utc().format(),
                            },
                        })
                    }
                />
                <ConnectAlert
                    connect_type_id={stats.connect_type_id}
                    charges_enabled={stats.charges_enabled}
                    payouts_enabled={stats.payouts_enabled}
                    onboarding_complete={stats.onboarding_complete}
                    requirements_due={stats.requirements_due}
                />

                {stats.product_count === 0 && (
                    <Alert
                        severity="warning"
                        variant="filled"
                        action={
                            <AlertButton
                                severity="warning"
                                onClick={() => props.history.push(`/events/${eventId}/tickets`)}
                            >
                                Create
                            </AlertButton>
                        }
                    >
                        Your event has 0 tickets
                    </Alert>
                )}
                <Alert
                    severity={alertType}
                    variant="filled"
                    style={publishStatus === 'concluded' ? { backgroundColor: '#777' } : undefined}
                    action={
                        publishStatus !== 'concluded' ? (
                            <AlertButton
                                severity={alertType}
                                onClick={() => props.history.push(`/events/${eventId}/publish`)}
                            >
                                Update
                            </AlertButton>
                        ) : undefined
                    }
                >
                    {alertText}
                </Alert>

                <PayoutAlert
                    stats={{ values: stats }}
                    event={event}
                    onClick={() => props.history.push(`payouts`)}
                />
            </Box>

            <GridContainer container spacing={4}>
                <Grid item xs={12} sm={6}>
                    <DashboardStatCard
                        title="Revenue"
                        query={revenueQuery}
                        formatValue={formatPrice}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DashboardStatCard title="Orders" query={ordersQuery} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DashboardStatCard title="Tickets Sold" query={ticketsSoldQuery} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DashboardStatCard title="Guest Check-ins" query={guestCheckinsQuery} />
                </Grid>

                <Grid item xs={12}>
                    <ProductSalesReport eventId={eventId} />
                </Grid>

                {Boolean(stats.seats_private_key && stats.seat_event_key) && (
                    <Grid item xs={12}>
                        <SeatingChart eventId={eventId} />
                    </Grid>
                )}
            </GridContainer>

            <RecentOrders eventId={eventId} />

            <GridContainer container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Top Cities" />
                        <CardContent>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell>CITY</TableHeadCell>
                                        <TableHeadCell align="right">ORDERS</TableHeadCell>
                                        <TableHeadCell align="right">TOTAL</TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {stats.topCities.length === 0 && (
                                        <TableRow>
                                            <TableCell align="center" colSpan={3}>
                                                <Typography variant="subtitle2">
                                                    No orders found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {stats.topCities.map((c) => (
                                        <TableRow key={`${c.city}-${c.province}`}>
                                            <TableCell>
                                                {c.city}, {c.province}
                                            </TableCell>
                                            <TableCell align="right">{c.order_count}</TableCell>
                                            <TableCell align="right">
                                                {formatPrice(c.revenue)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Grid>

                {event.scheduled_payout_flag === 1 && (
                    <Grid item xs={12} md={6}>
                        <EventPayouts eventId={eventId} />
                    </Grid>
                )}

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader title="Pricing Details" />
                        <CardContent>
                            <HostPricingDetails eventId={eventId} />
                        </CardContent>
                    </Card>
                </Grid>
            </GridContainer>
        </Page>
    );
}

function DashboardStatCard({ title, query, formatValue = (value) => value }) {
    const { data, isPending, isError } = query;

    return (
        <Card>
            <CardHeader title={title} />
            <CardContent>
                {isPending ? (
                    <Box display="flex" justifyContent="center" py={2}>
                        <CircularProgress size={24} />
                    </Box>
                ) : isError ? (
                    <Typography color="error">Unable to load stats</Typography>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={4}>
                            <Typography>Total</Typography>
                            <PrimaryStat>{formatValue(data.total)}</PrimaryStat>
                        </Grid>
                        <Grid item xs={6} lg={4}>
                            <Typography>Online</Typography>
                            <PrimaryStat>{formatValue(data.online)}</PrimaryStat>
                        </Grid>
                        <Grid item xs={6} lg={4}>
                            <Typography>In-Person</Typography>
                            <PrimaryStat>{formatValue(data.inPerson)}</PrimaryStat>
                        </Grid>
                    </Grid>
                )}
            </CardContent>
        </Card>
    );
}

function PayoutAlert(props) {
    const { stats, event, onClick } = props;

    if (stats.values.payout_amt && stats.values.payout_date) {
        // Event paid out (multi-payout event)
        if (event.scheduled_payout_flag) {
            return (
                <Alert
                    severity="success"
                    variant="filled"
                    action={
                        <Button color="inherit" size="small" onClick={onClick}>
                            View
                        </Button>
                    }
                >
                    {`Final payout issued on ${moment(stats.values.payout_date).format('MMM Do YYYY')}`}
                </Alert>
            );
            // Event paid out (single-payout event)
        } else {
            return (
                <Alert severity="success" variant="filled">
                    {`${formatPrice(stats.values.payout_amt)} paid out on ${moment(stats.values.payout_date).format('MMM Do YYYY')}`}
                </Alert>
            );
        }
        // Event not paid out
    } else {
        return null;
    }
}

const Alert = styled(_Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const GridContainer = styled(Grid)({
    marginBottom: '64px',
});

const PrimaryStat = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}));
PrimaryStat.defaultProps = { variant: 'h6' };

const TableHeadCell = styled(TableCell)(({ theme }) => ({
    ...theme.typography.subHeader,
}));
